import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Droplets, TrendingUp } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import { getIndustry, INDUSTRIES } from "@/data/industries";

export default function IndustryPage() {
  const { slug } = useParams();
  const industry = getIndustry(slug);
  const { setOpen } = useSimplify();

  useEffect(() => {
    if (industry) document.title = industry.seoTitle;
  }, [industry]);

  if (!industry) return <Navigate to="/" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Ocean Data for ${industry.name}`,
    provider: { "@type": "Organization", name: "Ocean Data", url: "https://oceandata.ca" },
    description: `${industry.headline} ${industry.intro}`,
    audience: { "@type": "BusinessAudience", name: industry.name },
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            {industry.eyebrow}
          </p>
          <h1
            data-testid="industry-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="industry-headline-line">{industry.headline}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {industry.intro}
          </motion.p>

          <div className="mt-16 mb-6">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold">
              Where the hours leak
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            {industry.leaks.map((leak, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  data-testid={`industry-leak-${i}`}
                  className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-9 h-9 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center">
                      <Droplets size={16} />
                    </span>
                    <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug">{leak.title}</h3>
                  </div>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{leak.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl bg-brand-tealsoft border border-brand-tealborder px-7 sm:px-9 py-7 flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-brand-teal text-white flex items-center justify-center shrink-0">
                <TrendingUp size={17} />
              </span>
              <p className="font-display font-medium text-lg text-brand-navy leading-snug">
                {industry.outcome}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6">
              <button
                data-testid="industry-cta-button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
              >
                Show me where we can simplify
                <ArrowRight size={18} />
              </button>
              <p className="text-sm text-slate-500">
                See it working first:{" "}
                <Link to="/demos" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
                  live demos
                </Link>
                {" · "}
                <Link to="/how-it-works" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
                  the 30-day trial
                </Link>
                {INDUSTRIES.filter((i) => i.slug !== slug).map((i) => (
                  <span key={i.slug}>
                    {" · "}
                    <Link to={`/industries/${i.slug}`} className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
                      for {i.name.toLowerCase()}
                    </Link>
                  </span>
                ))}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
